import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import {
  MapPin,
  Clock,
  GraduationCap,
  ChevronRight,
} from 'lucide-react';

import type { Job } from '@/modules/jobs/types/job';

interface CareerJobCardProps {
  job: Job;
  onClick: () => void;
}

const formatEmploymentType = (type: string) => {
  switch (type) {
    case 'FULL_TIME':
      return 'Full Time';

    case 'PART_TIME':
      return 'Part Time';

    case 'INTERNSHIP':
      return 'Internship';

    case 'CONTRACT':
      return 'Contract';

    case 'FREELANCE':
      return 'Freelance';

    default:
      return type;
  }
};

const formatSalary = (job: Job) => {
  if (
    job.minimumSalary === null ||
    job.minimumSalary === undefined ||
    job.maximumSalary === null ||
    job.maximumSalary === undefined
  ) {
    return 'Not Disclosed';
  }

  const normalizeToLPA = (
    value: number | string
  ) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return null;
    }

    // Already in LPA
    // Example: 12 -> 12 LPA
    // Example: 15 -> 15 LPA
    if (
      amount >= 1 &&
      amount < 1000
    ) {
      return amount;
    }

    // Annual salary
    // Example: 1200000 -> 12 LPA
    // Example: 1500000 -> 15 LPA
    if (amount >= 1000) {
      return amount / 100000;
    }

    // Existing incorrectly stored decimal value
    // Example: 0.00012 -> 12 LPA
    // Example: 0.00015 -> 15 LPA
    if (
      amount > 0 &&
      amount < 1
    ) {
      return amount * 100000;
    }

    return null;
  };

  const min = normalizeToLPA(
    job.minimumSalary
  );

  const max = normalizeToLPA(
    job.maximumSalary
  );

  if (
    min === null ||
    max === null
  ) {
    return 'Not Disclosed';
  }

  const currency =
    String(
      job.currency || 'INR'
    )
      .trim()
      .toUpperCase();

  const symbol =
    currency === 'USD'
      ? '$'
      : currency === 'EUR'
        ? '€'
        : currency === 'GBP'
          ? '£'
          : currency === 'AED'
            ? 'د.إ'
            : '₹';

  const formatValue = (
    value: number
  ) => {
    return Number.isInteger(value)
      ? String(value)
      : value
          .toFixed(2)
          .replace(/0+$/, '')
          .replace(/\.$/, '');
  };

  return `${symbol}${formatValue(min)}-${formatValue(max)} LPA`;
};

const formatExperience = (job: Job) => {
  if (job.maximumExperience) {
    return `${job.minimumExperience}-${job.maximumExperience} Years`;
  }

  return `${job.minimumExperience}+ Years`;
};

const getPostedDate = (
  createdAt: string
) => {
  const created = new Date(
    createdAt
  );

  const today = new Date();

  const diff = Math.floor(
    (today.getTime() -
      created.getTime()) /
      (1000 * 60 * 60 * 24)
  );

  if (diff === 0) {
    return 'Today';
  }

  if (diff === 1) {
    return '1 day ago';
  }

  return `${diff} days ago`;
};

export default function CareerJobCard({
  job,
  onClick,
}: CareerJobCardProps) {
  return (
    <Card
      onClick={onClick}
      className="group cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg transition-colors group-hover:text-blue-600">
              {job.title}
            </CardTitle>

            <CardDescription className="mt-1">
              {job.department}
            </CardDescription>
          </div>

          {job.priority === 'HIGH' && (
            <Badge
              variant="destructive"
              className="animate-pulse"
            >
              Urgent
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pb-4">
        <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            {job.location}
          </div>

          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {formatEmploymentType(
              job.employmentType
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <GraduationCap className="h-3.5 w-3.5" />
            {formatExperience(job)}
          </div>

          <div className="flex items-center gap-1.5">
            {formatSalary(job)}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {getPostedDate(
              job.createdAt
            )}
          </span>

          <ChevronRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-blue-600" />
        </div>
      </CardContent>
    </Card>
  );
}